// src/components/forms/BillPayDialog.tsx — pay a contractor bill, with the funding split shown first.
//
// Nothing here decides the split: every change of amount or the deposit toggle re-asks
// POST /bills/:id/pay-preview, and Confirm sends the same inputs to /pay, which runs
// the same computation under a row lock. The dialog only makes the outcome visible.
import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { billsApi } from '../../lib/api';
import { useCan } from '../../hooks/usePermission';
import { Button, Field, Input, Modal, Money, Textarea } from '../ui';
import { FormError, toMajor, toMinor } from './common';
import type { Bill } from '../../types/api';

const today = () => new Date().toISOString().slice(0, 10);

/** Debounce typed input so the preview is asked once the user pauses. */
function useDebounced<T>(value: T, ms = 350): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function BillPayDialog({ open, onClose, bill, onPaid }: { open: boolean; onClose: () => void; bill: Bill; onPaid?: (message: string) => void }) {
  const qc = useQueryClient();
  const canUseDeposits = useCan('bills.payFromDeposit');
  const [amount, setAmount] = useState(toMajor(bill.outstandingMinor));
  const [useDeposits, setUseDeposits] = useState(false);
  const [paidOn, setPaidOn] = useState(today());
  const [note, setNote] = useState('');

  const amountMinor = toMinor(useDebounced(amount));
  const amountValid = amountMinor !== undefined && amountMinor > 0 && amountMinor <= bill.outstandingMinor;

  const preview = useQuery({
    queryKey: ['bill-pay-preview', bill.id, amountMinor, useDeposits],
    queryFn: () => billsApi.payPreview(bill.id, { amountMinor, useDeposits: canUseDeposits ? useDeposits : undefined }),
    enabled: open && amountValid,
    retry: false,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
  const split = preview.data;

  const pay = useMutation({
    mutationFn: async () => (await billsApi.pay(bill.id, {
      paidOn, amountMinor, useDeposits: canUseDeposits ? useDeposits : undefined, note: note.trim() || undefined,
    })).data,
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['bill', bill.id] });
      void qc.invalidateQueries({ queryKey: ['/bills'] });
      void qc.invalidateQueries({ queryKey: ['/property-funds'] });
      void qc.invalidateQueries({ queryKey: ['/property-funds/advances'] });
      void qc.invalidateQueries({ queryKey: ['property-balance', bill.propertyId] });
      const s = r.split;
      const parts = [`${fmt(s.fromPropertyMinor, s.currency)} from the property`];
      if (s.fromDepositMinor > 0) parts.push(`${fmt(s.fromDepositMinor, s.currency)} from tenant deposits`);
      if (s.fromCompanyMinor > 0) parts.push(`${fmt(s.fromCompanyMinor, s.currency)} advanced by ${s.opcoEntityName ?? 'the company'}`);
      onPaid?.(`Paid ${fmt(s.amountMinor, s.currency)} on ${bill.number}: ${parts.join(', ')}.`);
      onClose();
    },
  });

  const blockedBySignoff = !!split && split.requiresSignoff && !split.signedOff && !split.canSignOff;
  const stale = toMinor(amount) !== split?.amountMinor || amountMinor !== split?.amountMinor || (canUseDeposits && useDeposits !== split?.useDeposits);
  const canConfirm = amountValid && !!split && !preview.isFetching && !stale && !blockedBySignoff && !pay.isPending;

  return (
    <Modal open={open} onClose={onClose} title={`Pay ${bill.number}`} size="lg"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => pay.mutate()} disabled={!canConfirm}>{pay.isPending ? 'Paying…' : 'Confirm payment'}</Button></>}>
      <div className="space-y-4">
        <p className="text-[13.5px] leading-relaxed text-ink-600">
          {bill.vendorName ?? 'No vendor'} · {bill.propertyName}. Outstanding <Money minor={bill.outstandingMinor} currency={bill.currency} className="font-medium text-ink-900" /> of <Money minor={bill.totalMinor} currency={bill.currency} />.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={`Amount (${bill.currency})`} hint={!amountValid && amount.trim() ? `Between 0.01 and ${toMajor(bill.outstandingMinor)}` : 'Defaults to the full outstanding amount'}>
            <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className="w-full" />
          </Field>
          <Field label="Paid on"><Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} className="w-full" /></Field>
        </div>

        {canUseDeposits && (
          <label className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50/70 px-3.5 py-3 text-[12.5px] text-amber-900">
            <input type="checkbox" className="mt-0.5" checked={useDeposits} onChange={(e) => setUseDeposits(e.target.checked)} />
            <span>
              <span className="font-medium">Use tenant deposit money</span>
              <span className="mt-0.5 block text-amber-800">This is tenants' money held against their tenancies, not the property's. Only use it if the shortfall cannot wait for the group company or the next rent.</span>
            </span>
          </label>
        )}

        {/* The split */}
        <div className="rounded-xl border border-ink-200">
          <div className="flex items-center justify-between border-b border-ink-100 px-4 py-2.5">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">How it is funded</span>
            {preview.isFetching && <Loader2 className="h-4 w-4 animate-spin text-bronze-400" />}
          </div>
          {!amountValid ? (
            <p className="px-4 py-4 text-[12.5px] text-ink-400">Enter an amount to see the split.</p>
          ) : !split ? (
            preview.isError ? <div className="p-4"><FormError error={preview.error} /></div> : <p className="px-4 py-4 text-[12.5px] text-ink-400">Working out the split…</p>
          ) : (
            <dl className="divide-y divide-ink-100 text-[13px]">
              <SplitRow label="From property balance" hint={`${split.propertyName} has ${fmt(split.availableForBillMinor, split.currency)} available for this bill`} minor={split.fromPropertyMinor} currency={split.currency} />
              {(canUseDeposits || split.fromDepositMinor > 0) && (
                <SplitRow label="From tenant deposits" hint={`${fmt(split.balance.depositsMinor, split.currency)} of deposits held${useDeposits ? '' : ' — not used'}`} minor={split.fromDepositMinor} currency={split.currency} warn={split.fromDepositMinor > 0} />
              )}
              <SplitRow
                label={split.sameEntity ? `Paid by ${split.balance.entityName ?? 'the company'} (property goes overdrawn)` : `Advanced by ${split.opcoEntityName ?? 'the group company'}`}
                hint={split.fromCompanyMinor > 0 && !split.sameEntity ? 'Recovered automatically from the next rent on the property' : undefined}
                minor={split.fromCompanyMinor} currency={split.currency} warn={split.fromCompanyMinor > 0} />
              <div className="flex items-center justify-between bg-ink-50/60 px-4 py-2.5 font-semibold text-ink-900">
                <dt>Total</dt><dd><Money minor={split.amountMinor} currency={split.currency} /></dd>
              </div>
            </dl>
          )}
        </div>

        {/* Director sign-off */}
        {split && (
          <div className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-3 text-[12.5px] ${blockedBySignoff ? 'border-red-200 bg-red-50 text-red-800' : 'border-ink-200 bg-ink-50/60 text-ink-700'}`}>
            {split.requiresSignoff && !split.signedOff ? <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> : <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />}
            <div>
              <p><span className="font-medium">Needs director sign-off:</span> {split.requiresSignoff ? 'Yes' : 'No'} <span className="text-ink-500">(limit {fmt(split.signoffLimitMinor, split.currency)})</span></p>
              {split.requiresSignoff && split.signedOff && <p className="mt-0.5">Already signed off.</p>}
              {split.requiresSignoff && !split.signedOff && split.canSignOff && <p className="mt-0.5">You can sign off — confirming this payment records your sign-off.</p>}
              {blockedBySignoff && <p className="mt-0.5 font-medium">A director (a user who can approve bills) must sign this off. You cannot confirm this payment.</p>}
            </div>
          </div>
        )}

        {split && split.warnings.length > 0 && (
          <ul className="space-y-1.5">
            {split.warnings.map((w) => (
              <li key={w} className="flex items-start gap-2 text-[12px] text-ink-600"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />{w}</li>
            ))}
          </ul>
        )}

        <Field label="Note (optional)"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="w-full" placeholder="Recorded on the payment and the audit trail" /></Field>

        {split && preview.isError && <FormError error={preview.error} />}
        <FormError error={pay.error} />
      </div>
    </Modal>
  );
}

function SplitRow({ label, hint, minor, currency, warn }: { label: string; hint?: string; minor: number; currency: string; warn?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 px-4 py-2.5">
      <dt className="min-w-0">
        <span className={`block ${warn ? 'font-medium text-amber-800' : 'text-ink-700'}`}>{label}</span>
        {hint && <span className="block text-[11.5px] text-ink-400">{hint}</span>}
      </dt>
      <dd className={`shrink-0 ${minor === 0 ? 'text-ink-300' : 'text-ink-900'}`}><Money minor={minor} currency={currency} /></dd>
    </div>
  );
}

const fmt = (minor: number, currency: string) => new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(minor / 100);
